import { LocalStorageConstants } from "@/types/LocalStorageConstants";
import React, { createContext, useCallback, useContext, useState } from "react";

export type TourPhase = "idle" | "my-services-collapsed" | "my-services-expanded" | "workflow-detail" | "complete";

interface FeatureTourContextValue {
  tourPhase: TourPhase;
  startTour: () => void;
  advancePhase: (phase: TourPhase) => void;
  markTourComplete: () => void;
  isTourComplete: boolean;
  isWorkflowDetailTourPending: boolean;
  scheduleWorkflowDetailTour: () => void;
  markWorkflowDetailTourComplete: () => void;
}

const SERVICES_KEY = LocalStorageConstants.FEATURE_TOUR_MY_SERVICES_COMPLETED;
const WORKFLOW_KEY = LocalStorageConstants.FEATURE_TOUR_WORKFLOW_DETAIL_PENDING;

const FeatureTourContext = createContext<FeatureTourContextValue | null>(null);

export function FeatureTourProvider({ children }: { children: React.ReactNode }) {
  const [tourPhase, setTourPhase] = useState<TourPhase>("idle");
  const [isTourComplete, setIsTourComplete] = useState(() => localStorage.getItem(SERVICES_KEY) === "true");
  const [isWorkflowDetailTourPending, setIsWorkflowDetailTourPending] = useState(() => localStorage.getItem(WORKFLOW_KEY) === "true");

  const startTour = useCallback(() => {
    setTourPhase("my-services-collapsed");
    setIsTourComplete(false);
  }, []);

  const advancePhase = useCallback((phase: TourPhase) => {
    setTourPhase(phase);
  }, []);

  const markTourComplete = useCallback(() => {
    localStorage.setItem(SERVICES_KEY, "true");
    localStorage.removeItem(WORKFLOW_KEY);
    setIsTourComplete(true);
    setIsWorkflowDetailTourPending(false);
    setTourPhase("idle");
  }, []);

  const scheduleWorkflowDetailTour = useCallback(() => {
    localStorage.setItem(SERVICES_KEY, "true");
    localStorage.setItem(WORKFLOW_KEY, "true");
    setIsTourComplete(true);
    setIsWorkflowDetailTourPending(true);
    setTourPhase("idle");
  }, []);

  const markWorkflowDetailTourComplete = useCallback(() => {
    localStorage.removeItem(WORKFLOW_KEY);
    setIsWorkflowDetailTourPending(false);
  }, []);

  return (
    <FeatureTourContext.Provider
      value={{
        tourPhase,
        startTour,
        advancePhase,
        markTourComplete,
        isTourComplete,
        isWorkflowDetailTourPending,
        scheduleWorkflowDetailTour,
        markWorkflowDetailTourComplete,
      }}
    >
      {children}
    </FeatureTourContext.Provider>
  );
}

export function useFeatureTour() {
  const ctx = useContext(FeatureTourContext);
  if (!ctx) throw new Error("useFeatureTour must be used within FeatureTourProvider");
  return ctx;
}
